/**
 * CellTypeTool.ts
 * Populates the Properties panel when a cell is selected.
 * Handles type/theme/floor/difficulty editing for octagons,
 * mood editing for connectors, and content placement section.
 */

import { Color3 } from '@babylonjs/core'
import type { GridCell, OctileCell, SquareConnector, CellType, CellTheme, FloorConfig, ConnectorMood } from '../types/OctileTypes.ts'
import { SAFE_ZONE_TYPES } from '../types/OctileTypes.ts'
import { getCell } from '../grid/OctileGrid.ts'
import type { GridRenderer } from '../grid/GridRenderer.ts'
import { CELL_TYPE_COLORS, THEME_FLOOR_COLORS } from '../grid/GridRenderer.ts'
import {
    CELL_TYPES_BY_CATEGORY, getCellTypeLabel, getThemeLabel, getFloorLabel, getMoodLabel,
    ALL_THEMES, ALL_FLOORS, ALL_MOODS
} from './CellTypeCatalog.ts'
import type { ContentTool, PlacedContent } from './ContentTool.ts'

export class CellTypeTool {
    private grid: GridCell[][]
    private renderer: GridRenderer
    private panelEl: HTMLElement
    private contentTool: ContentTool | null = null
    private currentRow = -1
    private currentCol = -1

    constructor(grid: GridCell[][], renderer: GridRenderer, panelEl: HTMLElement) {
        this.grid = grid
        this.renderer = renderer
        this.panelEl = panelEl
    }

    setContentTool(ct: ContentTool): void {
        this.contentTool = ct
    }

    showCell(row: number, col: number): void {
        this.currentRow = row
        this.currentCol = col

        const cell = getCell(this.grid, row, col)
        if (!cell || !cell.active) {
            this.clear()
            return
        }

        if (cell.kind === 'octagon') {
            this.renderOctagonPanel(cell)
        } else {
            this.renderConnectorPanel(cell)
        }
    }

    clear(): void {
        this.currentRow = -1
        this.currentCol = -1
        this.panelEl.innerHTML = `
            <div style="font-size: 12px; color: var(--text-tertiary);">
                Click a room to see its properties.
            </div>
        `
    }

    setGrid(grid: GridCell[][]): void {
        this.grid = grid
    }

    // ─── Octagon Panel ─────────────────────────────────

    private renderOctagonPanel(cell: OctileCell): void {
        const typeColor = this.getFloorColorHex(cell.type, cell.theme)

        this.panelEl.innerHTML = `
            <div class="prop-header">
                <span class="prop-badge" style="background: ${typeColor};">${getCellTypeLabel(cell.type).charAt(0)}</span>
                <span class="prop-coords">[${cell.row}, ${cell.col}]</span>
                <span class="prop-kind">Octagon</span>
            </div>

            <div class="control-row">
                <label class="control-label" for="propType">Type</label>
                <select id="propType" class="prop-select">
                    ${this.buildTypeOptions(cell.type)}
                </select>
            </div>

            <div class="control-row">
                <label class="control-label" for="propTheme">Theme</label>
                <select id="propTheme" class="prop-select">
                    ${ALL_THEMES.map(t =>
                        `<option value="${t}" ${t === cell.theme ? 'selected' : ''}>${getThemeLabel(t)}</option>`
                    ).join('')}
                </select>
            </div>

            <div class="control-row">
                <label class="control-label" for="propFloor">Floor</label>
                <select id="propFloor" class="prop-select">
                    ${ALL_FLOORS.map(f =>
                        `<option value="${f}" ${f === cell.floor ? 'selected' : ''}>${getFloorLabel(f)}</option>`
                    ).join('')}
                </select>
            </div>

            <div class="control-row">
                <label class="control-label" for="propDifficulty">Difficulty</label>
                <div class="prop-slider-row">
                    <input type="range" id="propDifficulty" min="1" max="10" value="${cell.difficulty}" class="prop-slider">
                    <span id="propDifficultyValue" class="prop-slider-value">${cell.difficulty}</span>
                </div>
            </div>
        `

        // Wire event listeners
        this.panelEl.querySelector('#propType')?.addEventListener('change', (e) => {
            const newType = (e.target as HTMLSelectElement).value as CellType
            this.updateCellType(cell, newType)
        })

        this.panelEl.querySelector('#propTheme')?.addEventListener('change', (e) => {
            const newTheme = (e.target as HTMLSelectElement).value as CellTheme
            cell.theme = newTheme
            this.updateFloorVisual(cell)
        })

        this.panelEl.querySelector('#propFloor')?.addEventListener('change', (e) => {
            cell.floor = (e.target as HTMLSelectElement).value as FloorConfig
        })

        this.panelEl.querySelector('#propDifficulty')?.addEventListener('input', (e) => {
            const val = parseInt((e.target as HTMLInputElement).value)
            cell.difficulty = val
            const label = this.panelEl.querySelector('#propDifficultyValue')
            if (label) label.textContent = String(val)
        })

        // Append content section
        this.renderContentSection(cell.row, cell.col)
    }

    // ─── Connector Panel ───────────────────────────────

    private renderConnectorPanel(cell: SquareConnector): void {
        this.panelEl.innerHTML = `
            <div class="prop-header">
                <span class="prop-badge" style="background: #473e36;">&#x25C7;</span>
                <span class="prop-coords">[${cell.row}, ${cell.col}]</span>
                <span class="prop-kind">Connector</span>
            </div>

            <div class="control-row">
                <label class="control-label" for="propMood">Mood</label>
                <select id="propMood" class="prop-select">
                    ${ALL_MOODS.map(m =>
                        `<option value="${m}" ${m === cell.mood ? 'selected' : ''}>${getMoodLabel(m)}</option>`
                    ).join('')}
                </select>
            </div>
        `

        this.panelEl.querySelector('#propMood')?.addEventListener('change', (e) => {
            cell.mood = (e.target as HTMLSelectElement).value as ConnectorMood
        })

        // Append content section (connectors have limited content: traps + decorations)
        this.renderContentSection(cell.row, cell.col)
    }

    // ─── Content Section ─────────────────────────────────

    private renderContentSection(row: number, col: number): void {
        if (!this.contentTool) return

        const section = document.createElement('div')
        section.className = 'content-section'

        const title = document.createElement('div')
        title.className = 'content-section-title'
        title.textContent = 'Content'
        section.appendChild(title)

        // Placed items list
        const placed = this.contentTool.getCellContent(row, col)
        if (placed.length > 0) {
            const placedEl = document.createElement('div')
            placedEl.className = 'placed-items'

            for (const p of placed) {
                const itemRow = document.createElement('div')
                itemRow.className = 'placed-item-row'
                itemRow.innerHTML = `
                    <span class="placed-item-icon">${p.item.icon}</span>
                    <span class="placed-item-label">${p.item.label}</span>
                    <span class="placed-item-cost">${p.item.energyCost}</span>
                `

                const removeBtn = document.createElement('button')
                removeBtn.className = 'placed-item-remove'
                removeBtn.textContent = '\u00D7'
                removeBtn.title = `Remove ${p.item.label}`
                removeBtn.addEventListener('click', () => {
                    this.contentTool!.removeContent(row, col, p.id, p.category)
                    // Re-render the panel to update the list
                    this.showCell(row, col)
                })
                itemRow.appendChild(removeBtn)
                placedEl.appendChild(itemRow)
            }

            section.appendChild(placedEl)
        } else {
            const empty = document.createElement('div')
            empty.className = 'content-empty'
            empty.textContent = 'No content placed'
            section.appendChild(empty)
        }

        // Place button (uses selected palette item)
        const selected = this.contentTool.getSelectedItem()
        if (selected) {
            const check = this.contentTool.canPlace(row, col, selected)
            const placeBtn = document.createElement('button')
            placeBtn.className = 'place-btn'
            placeBtn.disabled = !check.valid
            placeBtn.textContent = check.valid
                ? `+ Place ${selected.label} (${selected.energyCost})`
                : check.reason || 'Cannot place'
            placeBtn.title = check.valid
                ? `Place ${selected.label} here`
                : check.reason || ''

            placeBtn.addEventListener('click', () => {
                if (this.contentTool!.placeContent(row, col, selected)) {
                    this.showCell(row, col)
                }
            })
            section.appendChild(placeBtn)
        } else {
            const hint = document.createElement('div')
            hint.className = 'content-hint'
            hint.textContent = 'Select an item from Content Palette to place'
            section.appendChild(hint)
        }

        this.panelEl.appendChild(section)
    }

    // ─── Helpers ───────────────────────────────────────

    private buildTypeOptions(currentType: CellType): string {
        let html = ''
        for (const [category, types] of Object.entries(CELL_TYPES_BY_CATEGORY)) {
            html += `<optgroup label="${category}">`
            for (const t of types) {
                html += `<option value="${t}" ${t === currentType ? 'selected' : ''}>${getCellTypeLabel(t)}</option>`
            }
            html += `</optgroup>`
        }
        return html
    }

    /** Public entry point: set a cell's type by grid position */
    setCellType(row: number, col: number, type: CellType): boolean {
        const cell = getCell(this.grid, row, col)
        if (!cell?.active || cell.kind !== 'octagon') return false
        this.updateCellType(cell as OctileCell, type)
        return true
    }

    private updateCellType(cell: OctileCell, newType: CellType): void {
        cell.type = newType

        // Safe zone types clear spawns/traps
        if ((SAFE_ZONE_TYPES as readonly string[]).includes(newType)) {
            cell.spawns = []
            cell.traps = []
        }

        this.updateFloorVisual(cell)

        // Re-render to update badge color
        this.showCell(cell.row, cell.col)
    }

    private updateFloorVisual(cell: OctileCell): void {
        const color = CELL_TYPE_COLORS[cell.type] ?? THEME_FLOOR_COLORS[cell.theme] ?? THEME_FLOOR_COLORS.natural_cave
        this.renderer.updateFloorColor(cell.row, cell.col, color)
    }

    private getFloorColorHex(type: CellType, theme: CellTheme): string {
        const c = CELL_TYPE_COLORS[type] ?? THEME_FLOOR_COLORS[theme] ?? THEME_FLOOR_COLORS.natural_cave
        const r = Math.round(c.r * 255)
        const g = Math.round(c.g * 255)
        const b = Math.round(c.b * 255)
        return `rgb(${r}, ${g}, ${b})`
    }
}
